import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router"; // Use react-router-dom
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "@/firebase"; // Ensure you have Firebase initialized and auth exported
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"; // For feedback messages
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react"; // Icons for feedback

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    if (!email) {
      setError(t("forgotPassword.emailRequired"));
      setLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage(t("forgotPassword.successMessage"));
      setEmail(""); // Clear email field on success
    } catch (err: any) {
      // Handle specific Firebase errors if needed, otherwise show a generic message
      console.error("Password Reset Error:", err);
      // Consider more specific error mapping based on err.code if needed
      setError(t("forgotPassword.errorMessage"));
    } finally {
      setLoading(false);
    }
  };

  return (
    // Adjusted layout: Removed items-center, added pt-20 (adjust as needed)
    
      <Card className="w-full max-w-md"> {/* Removed self-center as parent handles centering */}
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">
            {t("forgotPassword.title")}
          </CardTitle>
          <CardDescription>
            {t("forgotPassword.description")}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleResetPassword}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("common.error")}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {message && (
              <Alert variant="default" className="bg-green-100 dark:bg-green-900 border-green-300 dark:border-green-700">
                 <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                 <AlertTitle className="text-green-800 dark:text-green-200">{t("common.success")}</AlertTitle>
                 <AlertDescription className="text-green-700 dark:text-green-300">{message}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t("emailAddress")}</Label>
              <Input
                id="email"
                type="email"
                className="text-right"
                placeholder={t("input.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading || !!message} // Disable input if loading or success message is shown
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 mt-2">
            <Button type="submit" className="w-full" disabled={loading || !!message}>
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t("buttons.submit")}
            </Button>
            <Button
              variant="link"
              className="text-sm text-gray-600 dark:text-gray-400"
              onClick={() => navigate("/auth")} // Adjust the path to your login page
              type="button" // Prevent form submission
              disabled={loading}
            >
              {t("buttons.backToLogin")}
            </Button>
          </CardFooter>
        </form>
      </Card>
  );
};

export default ForgotPasswordPage;